import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaMapMarkerAlt } from "react-icons/fa";
import { LuEye, LuEyeOff } from "react-icons/lu";
import LongTermRangePicker from "../LongTermRangePicker";
import usePublicAvailability from "../../hooks/usePublicAvailability";
import { checkAvailability } from "../../api/bookings";
import {
  getClosedWeekdays,
  getWorkingWindowForDate,
  isBlockedDate,
} from "../../utils/locationUtils";
import { normalizeDateValue, resolveSelectableDate, toDateKey } from "../../utils/dateUtils";
import {
  DATE_TIME_PICKER_MAX_MINUTES,
  DATE_TIME_PICKER_STEP,
} from "../../utils/dateTimePickerConstants";
import ProfileModal from "./ProfileModal";
import PhoneInput from "../PhoneInput";

const AVAILABILITY_DEBOUNCE_MS = 450;
const TECHNICAL_ERROR_PATTERN = /\b(?:Exception|StackTrace|System\.|Microsoft\.)\b|(?:\bat\s+[A-Za-z_$.][\w$.<>]*\()|(?:\bin\s+\S+:\d+)/i;

const resolveSafeErrorMessage = (error, fallbackMessage) => {
  const raw = typeof error?.message === "string" ? error.message.trim() : "";
  if (!raw) return fallbackMessage;
  if (raw.length > 220) return fallbackMessage;
  if (TECHNICAL_ERROR_PATTERN.test(raw)) return fallbackMessage;
  return raw;
};

const getMinutesOfDay = (date) =>
  date instanceof Date && !Number.isNaN(date.getTime())
    ? date.getHours() * 60 + date.getMinutes()
    : 0;

const isSameDay = (left, right) =>
  left instanceof Date &&
  right instanceof Date &&
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const clampDateToWindow = (date, minMinutes, maxMinutes) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return date;
  const safeMin = Number.isFinite(minMinutes) ? minMinutes : 0;
  const safeMax = Number.isFinite(maxMinutes)
    ? Math.max(safeMin, maxMinutes)
    : DATE_TIME_PICKER_MAX_MINUTES;
  const next = new Date(date);
  const currentMinutes = getMinutesOfDay(next);
  const clampedMinutes = Math.max(safeMin, Math.min(currentMinutes, safeMax));
  next.setHours(Math.floor(clampedMinutes / 60), clampedMinutes % 60, 0, 0);
  return next;
};

export const EditProfileModal = ({
  isOpen,
  formData,
  error,
  isSaving,
  onChange,
  onPhoneChange,
  onSubmit,
  onCancel,
  t,
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <ProfileModal
      title={t("profilePage.edit.title", "Edit profile details")}
      onClose={onCancel}
      descriptionId="profile-edit-description"
    >
      <form className="profile-form profile-form--modal" onSubmit={onSubmit}>
        <p id="profile-edit-description" className="profile-modal__lead">
          {t(
            "profilePage.edit.lead",
            "Keep your contact details current so we can reach you about your adventures."
          )}
        </p>
        <div className="profile-form__field">
          <label htmlFor="profile-name">
            {t("profilePage.edit.fields.name", "Name")}
          </label>
          <input
            id="profile-name"
            name="name"
            type="text"
            value={formData?.name ?? ""}
            onChange={onChange}
            autoComplete="name"
            maxLength={30}
            required
          />
        </div>
        <div className="profile-form__field">
          <label htmlFor="profile-phone">
            {t("profilePage.edit.fields.phone", "Phone number")}
          </label>
          <PhoneInput
            id="profile-phone"
            name="phone"
            value={formData?.phone ?? ""}
            onChange={onPhoneChange}
            autoComplete="tel"
            placeholder={t("profilePage.edit.fields.phonePlaceholder", "Phone number")}
            className="profile-phone"
            selectClassName="profile-phone__select"
            inputClassName="profile-phone__input"
            countryLabel={t("profilePage.edit.fields.countryCode", "Country code")}
            numberLabel={t("profilePage.edit.fields.phone", "Phone number")}
          />
        </div>
        {error && <p className="profile-form__error">{error}</p>}
        <div className="profile-form__actions">
          <button type="button" className="profile-btn profile-btn--ghost" onClick={onCancel}>
            {t("profilePage.actions.cancel", "Never mind")}
          </button>
          <button
            type="submit"
            className="profile-btn profile-btn--primary"
            disabled={isSaving}
          >
            {isSaving
              ? t("profilePage.edit.saveLoading", "Saving...")
              : t("profilePage.edit.save", "Save profile")}
          </button>
        </div>
      </form>
    </ProfileModal>
  );
};

export const PasswordModal = ({
  isOpen,
  passwordForm,
  passwordVisibility,
  passwordError,
  isSaving,
  onInputChange,
  onToggleVisibility,
  onSubmit,
  onCancel,
  t,
}) => {
  if (!isOpen) {
    return null;
  }

  const fields = [
    {
      key: "current",
      name: "current",
      label: t("profilePage.password.fields.current", "Current password"),
      autoComplete: "current-password",
    },
    {
      key: "next",
      name: "next",
      label: t("profilePage.password.fields.new", "New password"),
      autoComplete: "new-password",
    },
    {
      key: "confirm",
      name: "confirm",
      label: t("profilePage.password.fields.confirm", "Confirm new password"),
      autoComplete: "new-password",
    },
  ];

  const buildToggleLabel = (isVisible) =>
    isVisible
      ? t("profilePage.password.hide", "Hide password")
      : t("profilePage.password.show", "Show password");

  return (
    <ProfileModal
      title={t("profilePage.password.title", "Change password")}
      onClose={onCancel}
      descriptionId="profile-password-description"
    >
      <form className="profile-form profile-form--modal" onSubmit={onSubmit}>
        {fields.map((field) => (
          <div key={field.key} className="profile-form__field profile-form__field--password">
            <label htmlFor={`profile-${field.key}-password`}>{field.label}</label>
            <div className="profile-form__input-wrapper">
              <input
                id={`profile-${field.key}-password`}
                name={field.name}
                type={passwordVisibility[field.name] ? "text" : "password"}
                value={passwordForm[field.name]}
                onChange={onInputChange}
                autoComplete={field.autoComplete}
                maxLength={64}
                required
              />
              <button
                type="button"
                className="profile-form__password-toggle"
                onClick={() => onToggleVisibility(field.name)}
                aria-label={buildToggleLabel(passwordVisibility[field.name])}
                aria-pressed={passwordVisibility[field.name]}
                title={buildToggleLabel(passwordVisibility[field.name])}
              >
                {passwordVisibility[field.name] ? (
                  <LuEyeOff aria-hidden="true" />
                ) : (
                  <LuEye aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        ))}
        {passwordError && <p className="profile-form__error">{passwordError}</p>}
        <div className="profile-form__actions">
          <button type="button" className="profile-btn profile-btn--ghost" onClick={onCancel}>
            {t("profilePage.actions.cancel", "Never mind")}
          </button>
          <button
            type="submit"
            className="profile-btn profile-btn--primary"
            disabled={isSaving}
          >
            {isSaving
              ? t("profilePage.password.saveLoading", "Updating...")
              : t("profilePage.password.save", "Update password")}
          </button>
        </div>
        <p className="profile-form__note">
          {t(
            "profilePage.password.hint",
            "Use at least 9 characters, including an uppercase letter, a number, and a special character."
          )}
        </p>
      </form>
    </ProfileModal>
  );
};

export const CalendarModal = ({
  isOpen,
  calendarLabel,
  daysOfWeek,
  calendarWeeks,
  bookingsByDate,
  calendarSelectedDate,
  calendarSelectedBookings,
  selectedDateHeading,
  onClose,
  onGoToMonth,
  onSelectDate,
  onOpenReschedule,
  onOpenCancel,
  todayIso,
  t,
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <ProfileModal
      title={t("profilePage.sections.upcoming.calendar.title", "Bookings calendar")}
      onClose={onClose}
      descriptionId="profile-calendar-description"
      className="profile-modal--calendar"
    >
      <div className="profile-calendar">
        <div className="profile-calendar__toolbar">
          <button
            type="button"
            className="profile-calendar__nav"
            onClick={() => onGoToMonth(-1)}
            aria-label={t("profilePage.sections.upcoming.calendar.prevMonth", "Previous month")}
          >
            <svg viewBox="0 0 320 512" aria-hidden="true">
              <path d="M34.52 239.03 228.87 44.69c9.37-9.37 24.57-9.37 33.94 0l22.67 22.67c9.36 9.36 9.37 24.52.04 33.9L131.49 256l154.02 154.75c9.34 9.38 9.32 24.54-.04 33.9l-22.67 22.67c-9.37 9.37-24.57 9.37-33.94 0L34.52 272.97c-9.37-9.37-9.37-24.57 0-33.94z" />
            </svg>
          </button>
          <h4>{calendarLabel}</h4>
          <button
            type="button"
            className="profile-calendar__nav"
            onClick={() => onGoToMonth(1)}
            aria-label={t("profilePage.sections.upcoming.calendar.nextMonth", "Next month")}
          >
            <svg viewBox="0 0 320 512" aria-hidden="true">
              <path d="M285.476 272.971 91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z" />
            </svg>
          </button>
        </div>
        <p id="profile-calendar-description" className="profile-calendar__hint">
          {t(
            "profilePage.sections.upcoming.calendar.hint",
            "Pick a date to review, reschedule, or cancel your booking."
          )}
        </p>
        <div className="profile-calendar__grid">
          <div className="profile-calendar__row profile-calendar__row--labels">
            {daysOfWeek.map((day) => (
              <div key={day} className="profile-calendar__cell profile-calendar__cell--label">
                {day}
              </div>
            ))}
          </div>
          {calendarWeeks.map((week, weekIndex) => (
            <div className="profile-calendar__row" key={`week-${weekIndex}`}>
              {week.map((cell, cellIndex) => {
                if (!cell) {
                  return (
                    <div
                      key={`empty-${weekIndex}-${cellIndex}`}
                      className="profile-calendar__cell profile-calendar__cell--empty"
                      aria-hidden="true"
                    />
                  );
                }
                const hasBookings = Boolean(bookingsByDate[cell.isoDate]?.length);
                const isSelected = calendarSelectedDate === cell.isoDate;
                const isToday = todayIso === cell.isoDate;
                return (
                  <button
                    type="button"
                    key={cell.isoDate}
                    className={`profile-calendar__cell profile-calendar__cell--day${
                      isSelected ? " is-selected" : ""
                    }${isToday ? " is-today" : ""}${hasBookings ? " has-booking" : ""}`}
                    onClick={() => onSelectDate(cell.isoDate)}
                  >
                    <span className="profile-calendar__date">{cell.dayNumber}</span>
                    {/* No "Today" label; use styling instead. */}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <div className="profile-calendar__details">
          <h4>{selectedDateHeading}</h4>
          {calendarSelectedDate ? (
            calendarSelectedBookings.length ? (
              <ul className="profile-calendar__list">
                {calendarSelectedBookings.map((booking) => (
                  <li key={booking.id}>
                    <div className="profile-calendar__list-body">
                      <strong>{booking.type}</strong>
                      <p>
                        {booking.date} · {booking.time} · {booking.duration}
                      </p>
                      {booking.locationMapUrl ? (
                        <a
                          className="profile-calendar__location"
                          href={booking.locationMapUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <FaMapMarkerAlt aria-hidden="true" />
                          {booking.location}
                        </a>
                      ) : (
                        <span className="profile-calendar__location">
                          <FaMapMarkerAlt aria-hidden="true" />
                          {booking.location}
                        </span>
                      )}
                    </div>
                    <div className="profile-calendar__actions">
                      {!booking.isEventBooking && (
                        <button
                          type="button"
                          className="profile-chip profile-chip--primary"
                          onClick={() => onOpenReschedule(booking)}
                        >
                          {t("profilePage.sections.upcoming.reschedule", "Reschedule")}
                        </button>
                      )}
                      <button
                        type="button"
                        className="profile-chip profile-chip--alert"
                        onClick={() => onOpenCancel(booking)}
                      >
                        {t("profilePage.sections.upcoming.cancel", "Cancel")}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="profile-calendar__empty">
                {t("profilePage.sections.upcoming.calendar.empty", "No bookings on this day.")}
              </p>
            )
          ) : (
            <p className="profile-calendar__empty">
              {t(
                "profilePage.sections.upcoming.calendar.pickDay",
                "Choose a date to see what’s scheduled."
              )}
            </p>
          )}
        </div>
      </div>
    </ProfileModal>
  );
};

export const BookingQrModal = ({
  isOpen,
  booking,
  actionUrl,
  qrCodeDataUrl,
  expiresUtc,
  isLoading,
  error,
  onClose,
  t,
}) => {
  if (!isOpen) {
    return null;
  }

  const bookingLabel = booking?.type ?? t("profilePage.sections.upcoming.qrFallback", "Booking");

  return (
    <ProfileModal
      title={t("profilePage.sections.upcoming.qrTitle", "Pickup & return QR")}
      onClose={onClose}
      descriptionId="profile-qr-description"
    >
      <div className="profile-qr">
        <p id="profile-qr-description" className="profile-modal__lead">
          {t(
            "profilePage.sections.upcoming.qrLead",
            "Show this QR at pickup and return so staff can check out or check in your gear."
          )}
        </p>
        <div className="profile-qr__card">
          <h4>{bookingLabel}</h4>
          {isLoading ? (
            <p>{t("profilePage.sections.upcoming.qrLoading", "Generating your QR...")}</p>
          ) : error ? (
            <p className="profile-form__error">{error}</p>
          ) : (
            <>
              {qrCodeDataUrl && (
                <div className="profile-qr__frame">
                  <img
                    src={qrCodeDataUrl}
                    alt={t("profilePage.sections.upcoming.qrAlt", "Booking QR")}
                  />
                </div>
              )}
              {actionUrl && (
                <p className="profile-qr__link">
                  <a href={actionUrl}>{actionUrl}</a>
                </p>
              )}
              {expiresUtc && (
                <p className="profile-qr__meta">
                  {t("profilePage.sections.upcoming.qrExpires", "Valid until")}{" "}
                  {new Date(expiresUtc).toLocaleString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  })}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </ProfileModal>
  );
};

export const RescheduleModal = ({
  isOpen,
  booking,
  location,
  onClose,
  onSave,
  isSaving = false,
  error = "",
  t,
}) => {
  const isReady = Boolean(isOpen && booking);
  const { seasonStartDate, seasonEndDate } = usePublicAvailability();
  const initialStartDate = useMemo(() => {
    if (booking?.startUtc && booking.startUtc.length) {
      const next = new Date(booking.startUtc);
      if (!Number.isNaN(next.getTime())) return next;
    }
    if (booking?.date && booking?.time) {
      const next = new Date(`${booking.date}T${booking.time}`);
      if (!Number.isNaN(next.getTime())) return next;
    }
    return new Date();
  }, [booking?.date, booking?.startUtc, booking?.time]);
  const durationMs = useMemo(() => {
    if (typeof booking?.durationMs === "number" && booking.durationMs > 0) {
      return booking.durationMs;
    }
    return 90 * 60 * 1000;
  }, [booking?.durationMs]);
  const initialEndDate = useMemo(() => {
    if (booking?.endUtc && booking.endUtc.length) {
      const next = new Date(booking.endUtc);
      if (!Number.isNaN(next.getTime())) return next;
    }
    return new Date(initialStartDate.getTime() + durationMs);
  }, [booking?.endUtc, durationMs, initialStartDate]);

  const [rangeStartDate, setRangeStartDate] = useState(initialStartDate);
  const [rangeEndDate, setRangeEndDate] = useState(initialEndDate);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState("");
  const [availabilityResult, setAvailabilityResult] = useState(null);

  useEffect(() => {
    if (!isReady) return;
    setRangeStartDate(initialStartDate);
    setRangeEndDate(initialEndDate);
    setAvailabilityLoading(false);
    setAvailabilityError("");
    setAvailabilityResult(null);
  }, [booking?.id, initialEndDate, initialStartDate, isReady]);

  const bookingItemsPayload = useMemo(() => {
    if (!Array.isArray(booking?.items)) return [];
    return booking.items
      .filter((item) => item?.equipmentTypeId && Number(item.quantity) > 0)
      .map((item) => ({
        equipmentTypeId: item.equipmentTypeId,
        quantity: Number(item.quantity),
      }));
  }, [booking?.items]);

  const closedWeekdays = useMemo(
    () => getClosedWeekdays(location?.operatingHours ?? ""),
    [location?.operatingHours]
  );
  const blockedDates = useMemo(() => location?.blockedDates ?? [], [location?.blockedDates]);
  const blockedDateSet = useMemo(
    () =>
      new Set(
        (Array.isArray(blockedDates) ? blockedDates : [])
          .map((value) => toDateKey(value))
          .filter(Boolean)
      ),
    [blockedDates]
  );
  const locationLeadTimeHours = Number(location?.bookingLeadTimeHours ?? 0) || 0;
  const locationLeadTimeStart = useMemo(() => {
    const next = new Date();
    next.setHours(next.getHours() + Math.max(locationLeadTimeHours, 0), next.getMinutes(), 0, 0);
    return next;
  }, [locationLeadTimeHours]);
  const locationLeadTimeStartMinutes = useMemo(
    () =>
      Math.min(
        DATE_TIME_PICKER_MAX_MINUTES,
        Math.ceil(getMinutesOfDay(locationLeadTimeStart) / DATE_TIME_PICKER_STEP) *
          DATE_TIME_PICKER_STEP
      ),
    [locationLeadTimeStart]
  );
  const selectableCalendarStartDate = useMemo(() => {
    const seasonStart = seasonStartDate ? new Date(seasonStartDate) : null;
    const baseDate =
      seasonStart && seasonStart > locationLeadTimeStart ? seasonStart : locationLeadTimeStart;
    const initialDay = normalizeDateValue(baseDate, false) ?? new Date(baseDate);
    const latestDay = seasonEndDate ? normalizeDateValue(seasonEndDate, false) : null;

    for (let offset = 0; offset < 366; offset += 1) {
      const candidate = new Date(initialDay);
      candidate.setDate(initialDay.getDate() + offset);
      if (latestDay && candidate > latestDay) break;
      if (blockedDateSet.has(toDateKey(candidate))) continue;
      if (closedWeekdays.includes((candidate.getDay() + 6) % 7)) continue;

      const window = getWorkingWindowForDate(location?.operatingHours ?? "", candidate);
      if (window.hasHours === false) continue;

      const dayMinMinutes = Math.max(
        window.minMinutes ?? 0,
        isSameDay(candidate, locationLeadTimeStart) ? locationLeadTimeStartMinutes : 0
      );

      if (dayMinMinutes <= (window.maxMinutes ?? DATE_TIME_PICKER_MAX_MINUTES)) {
        const result = new Date(candidate);
        result.setHours(Math.floor(dayMinMinutes / 60), dayMinMinutes % 60, 0, 0);
        return result;
      }
    }

    return baseDate;
  }, [
    blockedDateSet,
    closedWeekdays,
    location?.operatingHours,
    locationLeadTimeStart,
    locationLeadTimeStartMinutes,
    seasonEndDate,
    seasonStartDate,
  ]);
  const startWorkingWindow = useMemo(() => {
    const anchorDate = rangeStartDate ?? selectableCalendarStartDate ?? locationLeadTimeStart;
    const window = getWorkingWindowForDate(location?.operatingHours ?? "", anchorDate);
    return {
      minMinutes: window.minMinutes ?? 0,
      maxMinutes: window.maxMinutes ?? DATE_TIME_PICKER_MAX_MINUTES,
      hasHours: window.hasHours !== false,
    };
  }, [location?.operatingHours, locationLeadTimeStart, rangeStartDate, selectableCalendarStartDate]);
  const endWorkingWindow = useMemo(() => {
    const anchorDate = rangeEndDate ?? rangeStartDate ?? selectableCalendarStartDate ?? locationLeadTimeStart;
    const window = getWorkingWindowForDate(location?.operatingHours ?? "", anchorDate);
    return {
      minMinutes: window.minMinutes ?? 0,
      maxMinutes: window.maxMinutes ?? DATE_TIME_PICKER_MAX_MINUTES,
      hasHours: window.hasHours !== false,
    };
  }, [
    location?.operatingHours,
    locationLeadTimeStart,
    rangeEndDate,
    rangeStartDate,
    selectableCalendarStartDate,
  ]);
  const selectableStartMinMinutes = useMemo(
    () =>
      Math.max(
        startWorkingWindow.minMinutes,
        isSameDay(rangeStartDate, locationLeadTimeStart) ? locationLeadTimeStartMinutes : 0
      ),
    [locationLeadTimeStart, locationLeadTimeStartMinutes, rangeStartDate, startWorkingWindow.minMinutes]
  );
  const selectableStartMaxMinutes = startWorkingWindow.maxMinutes;
  const selectableEndMinMinutes = endWorkingWindow.minMinutes;
  const selectableEndMaxMinutes = endWorkingWindow.maxMinutes;

  useEffect(() => {
    if (!location) return;
    if (!(rangeStartDate instanceof Date) || Number.isNaN(rangeStartDate.getTime())) {
      if (rangeEndDate !== null) {
        setRangeEndDate(null);
      }
      return;
    }

    const nextStartDate = resolveSelectableDate({
      value: rangeStartDate,
      minDate: selectableCalendarStartDate,
      maxDate: seasonEndDate,
      disabledWeekdays: closedWeekdays,
      blockedDates: blockedDateSet,
    });

    if (!(nextStartDate instanceof Date) || Number.isNaN(nextStartDate.getTime())) return;

    let changed = false;
    let startNext = new Date(rangeStartDate);
    const startTimeOutOfBounds =
      getMinutesOfDay(startNext) < selectableStartMinMinutes ||
      getMinutesOfDay(startNext) > startWorkingWindow.maxMinutes;
    const startDateNeedsReset =
      !isSameDay(nextStartDate, rangeStartDate) ||
      startWorkingWindow.hasHours === false ||
      isBlockedDate(blockedDates, rangeStartDate) ||
      startNext.getTime() < locationLeadTimeStart.getTime();

    if (startDateNeedsReset) {
      const nextWindow = getWorkingWindowForDate(location.operatingHours ?? "", nextStartDate);
      startNext = new Date(nextStartDate);
      const nextStartMinMinutes = Math.max(
        nextWindow.minMinutes ?? 0,
        isSameDay(nextStartDate, locationLeadTimeStart) ? locationLeadTimeStartMinutes : 0
      );
      startNext.setHours(
        Math.floor(nextStartMinMinutes / 60),
        nextStartMinMinutes % 60,
        0,
        0
      );
      changed = true;
    } else if (startTimeOutOfBounds) {
      startNext = clampDateToWindow(
        startNext,
        selectableStartMinMinutes,
        selectableStartMaxMinutes
      );
      changed = true;
    }

    let endNext =
      rangeEndDate instanceof Date && !Number.isNaN(rangeEndDate.getTime())
        ? new Date(rangeEndDate)
        : null;
    const endTimeOutOfBounds =
      endNext &&
      (getMinutesOfDay(endNext) < selectableEndMinMinutes ||
        getMinutesOfDay(endNext) > selectableEndMaxMinutes);
    if (endNext && endWorkingWindow.hasHours && endTimeOutOfBounds) {
      endNext = clampDateToWindow(endNext, selectableEndMinMinutes, selectableEndMaxMinutes);
      changed = true;
    }
    const endNotAfterStartDay =
      endNext &&
      normalizeDateValue(endNext, false) <= normalizeDateValue(startNext, false);
    if (endNotAfterStartDay) {
      const candidateEndDate = new Date(startNext);
      candidateEndDate.setDate(candidateEndDate.getDate() + 1);
      const resolvedEndDate = resolveSelectableDate({
        value: candidateEndDate,
        minDate: candidateEndDate,
        maxDate: seasonEndDate,
        disabledWeekdays: closedWeekdays,
        blockedDates: blockedDateSet,
        fallbackValue: candidateEndDate,
      });

      if (!(resolvedEndDate instanceof Date) || Number.isNaN(resolvedEndDate.getTime())) {
        endNext = null;
      } else {
        endNext = new Date(resolvedEndDate);
        const nextEndWindow = getWorkingWindowForDate(location.operatingHours ?? "", endNext);
        if (nextEndWindow.hasHours) {
          endNext.setHours(
            Math.floor((nextEndWindow.minMinutes ?? 0) / 60),
            (nextEndWindow.minMinutes ?? 0) % 60,
            0,
            0
          );
        }
      }
      changed = true;
    }

    if (changed) {
      setRangeStartDate(startNext);
      setRangeEndDate(endNext);
    }
  }, [
    blockedDateSet,
    blockedDates,
    closedWeekdays,
    endWorkingWindow.hasHours,
    location,
    locationLeadTimeStart,
    locationLeadTimeStartMinutes,
    rangeEndDate,
    rangeStartDate,
    seasonEndDate,
    selectableCalendarStartDate,
    selectableEndMaxMinutes,
    selectableEndMinMinutes,
    selectableStartMaxMinutes,
    selectableStartMinMinutes,
    startWorkingWindow.hasHours,
    startWorkingWindow.maxMinutes,
  ]);

  const startUtc = useMemo(
    () => (rangeStartDate instanceof Date ? new Date(rangeStartDate) : null),
    [rangeStartDate]
  );
  const endUtc = useMemo(
    () => (rangeEndDate instanceof Date ? new Date(rangeEndDate) : null),
    [rangeEndDate]
  );
  const isTimeSlotValid = useMemo(() => {
    if (!startUtc || !endUtc) return false;
    if (endUtc <= startUtc) return false;
    if (isBlockedDate(blockedDates, startUtc) || isBlockedDate(blockedDates, endUtc)) {
      return false;
    }

    const startMinutes = getMinutesOfDay(startUtc);
    const endMinutes = getMinutesOfDay(endUtc);
    if (!startWorkingWindow.hasHours || !endWorkingWindow.hasHours) return false;
    if (startMinutes < startWorkingWindow.minMinutes || startMinutes > startWorkingWindow.maxMinutes) {
      return false;
    }
    if (endMinutes < endWorkingWindow.minMinutes || endMinutes > endWorkingWindow.maxMinutes) {
      return false;
    }
    if (startUtc.getTime() < locationLeadTimeStart.getTime()) return false;
    return endUtc.getTime() > startUtc.getTime();
  }, [
    blockedDates,
    endUtc,
    endWorkingWindow.hasHours,
    endWorkingWindow.maxMinutes,
    endWorkingWindow.minMinutes,
    locationLeadTimeStart,
    startUtc,
    startWorkingWindow.hasHours,
    startWorkingWindow.maxMinutes,
    startWorkingWindow.minMinutes,
  ]);

  const availabilityRequest = useMemo(() => {
    if (!isReady) return null;
    const locationId = booking?.locationId;
    if (!locationId) return null;
    if (!bookingItemsPayload.length) return null;
    if (!startUtc || !endUtc || !isTimeSlotValid) return null;
    const itemsKey = bookingItemsPayload
      .map((item) => `${item.equipmentTypeId}:${item.quantity}`)
      .join("|");

    return {
      key: `${locationId}|${startUtc.toISOString()}|${endUtc.toISOString()}|${itemsKey}`,
      payload: {
        locationId,
        startUtc: startUtc.toISOString(),
        endUtc: endUtc.toISOString(),
        items: bookingItemsPayload,
      },
    };
  }, [
    booking?.locationId,
    bookingItemsPayload,
    endUtc,
    isReady,
    isTimeSlotValid,
    startUtc,
  ]);

  useEffect(() => {
    if (!availabilityRequest) {
      setAvailabilityResult(null);
      setAvailabilityError("");
      setAvailabilityLoading(false);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled) return;
      setAvailabilityError("");
      setAvailabilityLoading(true);

      checkAvailability(availabilityRequest.payload)
        .then((result) => {
          if (!cancelled) {
            setAvailabilityResult(result);
          }
        })
        .catch((err) => {
          if (!cancelled) {
            setAvailabilityResult(null);
            setAvailabilityError(
              resolveSafeErrorMessage(
                err,
                t(
                  "profilePage.sections.upcoming.availabilityError",
                  "We couldn’t verify availability. Please try again."
                )
              )
            );
          }
        })
        .finally(() => {
          if (!cancelled) {
            setAvailabilityLoading(false);
          }
        });
    }, AVAILABILITY_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [availabilityRequest, t]);

  const needsAvailability = bookingItemsPayload.length > 0;
  const hasValidTimeWindow = Boolean(startUtc && endUtc && isTimeSlotValid);
  const canReschedule =
    hasValidTimeWindow &&
    (!needsAvailability ||
      (!availabilityLoading && !availabilityError && availabilityResult?.isAvailable));
  const rangeValidationMessage = !hasValidTimeWindow
    ? t(
      "profilePage.sections.upcoming.rescheduleOutsideHours",
      "Choose a valid rental range within this location’s available hours."
    )
    : "";

  const availabilityStatusNode = needsAvailability ? (
    <>
      {availabilityLoading && (
        <span className="profile-form__success">
          {t("profilePage.sections.upcoming.availabilityChecking", "Checking availability…")}
        </span>
      )}
      {!availabilityLoading && !availabilityError && availabilityResult && (
        <span
          className={availabilityResult.isAvailable ? "profile-form__success" : "profile-form__error"}
        >
          {availabilityResult.isAvailable
            ? t(
              "profilePage.sections.upcoming.availabilityAvailable",
              "Great news! Your selected gear is available."
            )
            : t(
              "profilePage.sections.upcoming.availabilityUnavailable",
              "Some gear is unavailable. Try another slot."
            )}
        </span>
      )}
      {!availabilityLoading && availabilityError && (
        <span className="profile-form__error" role="alert">
          {availabilityError}
        </span>
      )}
    </>
  ) : null;

  const handleSelectionChange = useCallback(({ startDate, endDate }) => {
    if (startDate instanceof Date && !Number.isNaN(startDate.getTime())) {
      setRangeStartDate(startDate);
    }
    if (endDate == null) {
      setRangeEndDate(null);
    } else if (endDate instanceof Date && !Number.isNaN(endDate.getTime())) {
      setRangeEndDate(endDate);
    }
  }, []);

  if (!isReady) {
    return null;
  }

  return (
    <ProfileModal
      title={t("profilePage.sections.upcoming.rescheduleTitle", "Reschedule booking")}
      onClose={onClose}
      descriptionId="profile-reschedule-description"
    >
      <div className="profile-modal__section">
        <p id="profile-reschedule-description" className="profile-modal__lead">
          {t(
            "profilePage.sections.upcoming.rescheduleLead",
            "Pick a new rental range for {type} at {location}.",
            { type: booking.type, location: booking.location }
          )}
        </p>
        <LongTermRangePicker
          startDate={rangeStartDate}
          endDate={rangeEndDate}
          onRangeChange={handleSelectionChange}
          selectableStartDate={selectableCalendarStartDate}
          selectableEndDate={seasonEndDate}
          minimumStartDateTime={locationLeadTimeStart}
          startMinMinutes={selectableStartMinMinutes}
          startMaxMinutes={selectableStartMaxMinutes}
          endMinMinutes={selectableEndMinMinutes}
          endMaxMinutes={selectableEndMaxMinutes}
          disabledWeekdays={closedWeekdays}
          blockedDates={blockedDates}
        />
        {rangeValidationMessage && <p className="profile-form__error">{rangeValidationMessage}</p>}
        {availabilityStatusNode && (
          <div className="profile-form__note profile-form__note--status">{availabilityStatusNode}</div>
        )}
        {error && <p className="profile-form__error">{error}</p>}
        <div className="profile-form__actions">
          <button type="button" className="profile-btn profile-btn--ghost" onClick={onClose}>
            {t("profilePage.actions.cancel", "Never mind")}
          </button>
          <button
            type="button"
            className="profile-btn profile-btn--primary"
            disabled={isSaving || !canReschedule}
            onClick={() => onSave?.({ startDate: rangeStartDate, endDate: rangeEndDate })}
          >
            {isSaving
              ? t("profilePage.sections.upcoming.saveRescheduleLoading", "Saving...")
              : t("profilePage.sections.upcoming.saveReschedule", "Save changes")}
          </button>
        </div>
      </div>
    </ProfileModal>
  );
};

export const CancelModal = ({
  isOpen,
  booking,
  onClose,
  onConfirm,
  isProcessing = false,
  error = "",
  t,
}) => {
  if (!isOpen || !booking) {
    return null;
  }

  return (
    <ProfileModal
      title={t("profilePage.sections.upcoming.cancelTitle", "Cancel booking")}
      onClose={onClose}
      descriptionId="profile-cancel-description"
    >
      <div className="profile-modal__section">
        <p id="profile-cancel-description" className="profile-modal__lead">
          {t(
            "profilePage.sections.upcoming.cancelLead",
            "Cancel {type} on {date} at {time}?",
            {
              type: booking.type,
              date: booking.displayDate ?? booking.date,
              time: booking.time,
            }
          )}
        </p>
        <div className="profile-form__actions">
          <button type="button" className="profile-btn profile-btn--ghost" onClick={onClose}>
            {t("profilePage.actions.keep", "Keep booking")}
          </button>
          <button
            type="button"
            className="profile-btn profile-btn--danger"
            onClick={onConfirm}
            disabled={isProcessing}
          >
            {isProcessing
              ? t("profilePage.sections.upcoming.cancelLoading", "Cancelling…")
              : t("profilePage.sections.upcoming.confirmCancel", "Cancel booking")}
          </button>
        </div>
        {error && <p className="profile-form__error">{error}</p>}
        <p className="profile-modal__note">
          {t(
            "profilePage.sections.upcoming.cancelNote",
            "Heads up: cancellations inside 24 hours may incur a fee."
          )}
        </p>
      </div>
    </ProfileModal>
  );
};
